/**
 * Utility functions for password validation that match server-side requirements
 */

/**
 * Validates a password according to role-specific requirements
 * @param {string} password - The password to validate
 * @param {string} role - The user role ('admin', 'manager', 'user', or 'lmsuser')
 * @returns {Object} Object with isValid flag and validation details for individual requirements
 */
export const validatePassword = (password, role) => {
  // Handle empty password
  if (!password) {
    return {
      isValid: false,
      requirements: {
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        specialChars: false
      },
      message: 'Password is required'
    };
  }

  // Define validation results object
  const result = {
    isValid: false,
    requirements: {
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      specialChars: false
    },
    message: ''
  };

  // Check if this is for lmsuser role (simplified requirements)
  if (role === 'lmsuser') {
    // LMS users only need 5+ character passwords
    result.requirements.length = password.length >= 5;
    // Other requirements don't apply
    result.requirements.lowercase = true;
    result.requirements.uppercase = true;
    result.requirements.number = true;
    result.requirements.specialChars = true;
    
    // Set overall validity
    result.isValid = result.requirements.length;
    
    // Set appropriate message
    if (!result.isValid) {
      result.message = 'Password must be at least 5 characters for LMS users';
    }
  } else {
    // Regular users have more stringent password requirements
    
    // Check length requirement (8+ characters)
    result.requirements.length = password.length >= 8;
    
    // Check lowercase letter requirement
    result.requirements.lowercase = /[a-z]/.test(password);
    
    // Check uppercase letter requirement
    result.requirements.uppercase = /[A-Z]/.test(password);
    
    // Check number requirement
    result.requirements.number = /\d/.test(password);
    
    // Check special characters (only certain ones allowed)
    const allowedSpecialChars = "@$!%*?&";
    const hasOnlyAllowedChars = [...password].every(char => {
      return /[a-zA-Z0-9]/.test(char) || allowedSpecialChars.includes(char);
    });
    result.requirements.specialChars = hasOnlyAllowedChars;
    
    // Set overall validity - all requirements must be met
    result.isValid = Object.values(result.requirements).every(Boolean);
    
    // Set appropriate message based on first failing requirement
    if (!result.requirements.length) {
      result.message = 'Password must be at least 8 characters';
    } else if (!result.requirements.lowercase) {
      result.message = 'Password must contain at least one lowercase letter';
    } else if (!result.requirements.uppercase) {
      result.message = 'Password must contain at least one uppercase letter';
    } else if (!result.requirements.number) {
      result.message = 'Password must contain at least one number';
    } else if (!result.requirements.specialChars) {
      result.message = 'Password can only contain letters, numbers, and special characters (@$!%*?&)';
    }
  }
  
  return result;
};

/**
 * Returns an array of password requirements based on the user role
 * @param {string} role - The user role ('admin', 'manager', 'user', or 'lmsuser')
 * @returns {Array} Array of requirement objects with id, text, and required properties
 */
export const getPasswordRequirements = (role) => {
  if (role === 'lmsuser') {
    // Simplified requirements for LMS users
    return [
      {
        id: 'length',
        text: 'At least 5 characters',
        required: true
      }
    ];
  } else {
    // Standard requirements for all other users
    return [
      {
        id: 'length',
        text: 'At least 8 characters',
        required: true
      },
      {
        id: 'lowercase',
        text: 'At least one lowercase letter (a-z)',
        required: true
      },
      {
        id: 'uppercase',
        text: 'At least one uppercase letter (A-Z)',
        required: true
      },
      {
        id: 'number',
        text: 'At least one number (0-9)',
        required: true
      },
      {
        id: 'specialChars',
        text: 'Only letters, numbers, and special characters (@$!%*?&)',
        required: true
      }
    ];
  }
};